import React from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useCheckoutStore } from '../../store/checkoutStore';

const templates = [
  {
    id: 'modern-split',
    name: 'Modern Split Panel',
    description: 'Professional split-screen layout with gradient background and modern styling',
    preview: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    features: [
      'Split-panel design',
      'Gradient background',
      'Product showcase',
      'Feature list',
      'Clean payment form',
      'Mobile responsive'
    ]
  },
  {
    id: 'simple-card',
    name: 'Simple Card',
    description: 'Clean, minimalist card layout focused on subscription options',
    preview: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    features: [
      'Subscription toggle',
      'Clean card layout',
      'Minimalist design',
      'Focus on pricing',
      'Simple payment form',
      'Mobile responsive'
    ]
  }
];

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export const TemplateSelector = ({ onNext, onBack }: Props) => {
  const { currentPage, updatePageSettings } = useCheckoutStore();
  const [selected, setSelected] = React.useState(currentPage?.templateId || '');

  const handleContinue = () => {
    if (selected) {
      updatePageSettings({ templateId: selected });
      onNext();
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-6">Choose Your Template</h2>
        <div className="grid grid-cols-2 gap-6">
          {templates.map(template => (
            <div
              key={template.id}
              onClick={() => setSelected(template.id)}
              className={`group cursor-pointer bg-white rounded-xl overflow-hidden border-2 transition-all ${
                selected === template.id
                  ? 'border-[#1E293B] ring-2 ring-[#1E293B] ring-opacity-10'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="aspect-[16/9] relative overflow-hidden">
                <img
                  src={template.preview}
                  alt={template.name}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                {selected === template.id && (
                  <div className="absolute top-4 right-4 w-8 h-8 bg-[#1E293B] rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    <p className="text-gray-500 mt-1">{template.description}</p>
                  </div>
                  {template.id === 'modern-split' && (
                    <span className="px-3 py-1 bg-[#1E293B] text-white text-sm font-medium rounded-full">
                      Recommended
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  {template.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4B86A]" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="btn btn-secondary flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!selected}
          className="btn flex items-center gap-2"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};